import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rows, type ID, type LetterTemplate } from '@hrms/shared';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { openHtml } from '../../../lib/files';
import { useMut } from '../../../lib/query';
import { useParams } from '../../../navigation/nav';
import { CrudList } from '../components/CrudList';
import { EmployeePicker } from '../components/EmployeePicker';
import { HtmlView } from '../components/HtmlView';
import { Button, Modal, Screen, Select, Text } from '../../../ui';

const PLACEHOLDERS = ['employee.full_name', 'employee.emp_code', 'employee.designation', 'employee.department', 'employee.date_of_joining', 'employee.ctc', 'tenant.name', 'today'];
const TYPES = ['offer', 'appointment', 'confirmation', 'promotion', 'transfer', 'relieving', 'experience', 'other'];

/** Letter templates CRUD + generate a letter for an employee (optional param { employee }). */
export function LettersScreen() {
  const empParam = useParams<{ employee: string }>().employee;
  const [gen, setGen] = useState<{ tpl: ID | null; emp: ID | null } | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const tpls = useQuery({ queryKey: ['letter-templates', 'all'], queryFn: () => api.letterTemplates.list({ per_page: 200 }) });
  useEffect(() => {
    if (empParam) setGen({ tpl: null, emp: Number(empParam) });
  }, [empParam]);
  const generate = useMut(() => api.letterTemplates.generate(gen!.tpl!, gen!.emp!), { onSuccess: (r) => setHtml(r.html) });
  return (
    <Screen testID="letters">
      <CrudList<LetterTemplate>
        title="Letter templates"
        singular="Template"
        description="HTML templates with placeholders like {{employee.full_name}}. Generate offer, appointment, confirmation or relieving letters for any employee."
        resource={api.letterTemplates}
        queryKey={['letter-templates']}
        modalWidth={820}
        headerActions={<Button icon="file" title="Generate letter" onPress={() => setGen({ tpl: null, emp: null })} testID="letters-generate" />}
        renderRowActions={(t) => <Button size="sm" icon="file" title="Generate" onPress={() => setGen({ tpl: t.id, emp: null })} />}
        columns={[
          { key: 'name', title: 'Name', width: 200, flex: 2 },
          { key: 'type', title: 'Type', width: 130, render: (r) => humanize(r.type), sortValue: (r) => r.type },
        ]}
        fields={[
          { key: 'name', label: 'Name', required: true },
          { key: 'type', label: 'Type', type: 'select', required: true, options: TYPES.map((t) => ({ value: t, label: humanize(t) })) },
          { key: 'body', label: 'HTML body', type: 'textarea', required: true, hint: `Placeholders: ${PLACEHOLDERS.map((p) => `{{${p}}}`).join(' ')}` },
        ]}
      />
      <Modal
        open={!!gen && !html}
        onClose={() => setGen(null)}
        title="Generate letter"
        width={520}
        footer={
          <>
            <Button title="Cancel" onPress={() => setGen(null)} />
            <Button variant="primary" title="Preview" disabled={!gen?.tpl || !gen?.emp} loading={generate.isPending} onPress={() => generate.mutate(undefined)} testID="letters-preview" />
          </>
        }
      >
        <Select label="Template" placeholder="Select template…" value={gen?.tpl ?? null} options={rows(tpls.data).map((t) => ({ value: t.id, label: `${t.name} · ${humanize(t.type)}` }))} onChange={(tpl) => setGen((g) => ({ emp: g?.emp ?? null, tpl }))} testID="letters-template" />
        <EmployeePicker label="Employee" value={gen?.emp} onChange={(emp) => setGen((g) => ({ tpl: g?.tpl ?? null, emp }))} testID="letters-employee" />
      </Modal>
      <Modal
        open={!!html}
        onClose={() => { setHtml(null); setGen(null); }}
        title="Letter preview"
        width={900}
        footer={
          <>
            <Button title="Back" onPress={() => setHtml(null)} />
            <Button variant="primary" icon="eye" title="Open / print" onPress={() => html && void openHtml(html)} />
          </>
        }
      >
        <Text variant="small">Rendered in a sandboxed frame — scripts are disabled.</Text>
        {html ? <HtmlView html={html} height={520} /> : null}
      </Modal>
    </Screen>
  );
}
